import * as actionCreators from './actionCreators';
import { ActionTypeNames } from './actions';
import { initialState, State, TableState } from './state';

type Actions = ReturnType<typeof actionCreators[keyof typeof actionCreators]>;

export default function reducer(state: State = initialState, action: Actions): State {
    switch (action.type) {
        case ActionTypeNames.Select: {
            if (state[action.tableKey].selection.find((id) => id === action.id) !== undefined) {
                return state;
            }
            const newTableState: TableState = {
                ...state[action.tableKey],
                selection: [...state[action.tableKey].selection, action.id]
            };
            return {
                ...state,
                [action.tableKey]: newTableState,
            };
        }
        case ActionTypeNames.Unselect: {
            const newTableState: TableState = {
                ...state[action.tableKey],
                selection: state[action.tableKey].selection.filter((id) => id !== action.id)
            };
            return {
                ...state,
                [action.tableKey]: newTableState,
            };
        }
        case ActionTypeNames.SelectAll: {
            const newTableState: TableState = {
              ...state[action.tableKey],
              selection: [...state[action.tableKey].selection, ...action.ids].filter((value, index, self) => self.indexOf(value) === index),
            };
            return {
                ...state,
                [action.tableKey]: newTableState,
            };
        }
        case ActionTypeNames.UnselectAll: {
            const newTableState: TableState = {
                ...state[action.tableKey],
                selection: state[action.tableKey].selection.filter((id) => !action.ids.includes(id))
            };
            return {
                ...state,
                [action.tableKey]: newTableState,
            };
        }
        case ActionTypeNames.ClearSelection: {
            const newTableState: TableState = {
                ...state[action.tableKey],
                selection: []
            };
            return {
                ...state,
                [action.tableKey]: newTableState,
            };
        }
        case ActionTypeNames.NextPage: {
            const oldPaging = state[action.tableKey].paging;
            if (oldPaging.currentPage - 1 < oldPaging.numberOfPages) {
                const newTableState: TableState = {
                    ...state[action.tableKey],
                    paging: {
                        ...oldPaging,
                        currentPage: Math.min(oldPaging.currentPage + 1, oldPaging.numberOfPages - 1),
                    },
                };
                return {
                    ...state,
                    [action.tableKey]: newTableState,
                };
            } else {
                return state;
            }
        }
        case ActionTypeNames.PrevPage: {
            const oldPaging = state[action.tableKey].paging;
            if (oldPaging.currentPage - 1 >= 0) {
                const newTableState: TableState = {
                    ...state[action.tableKey],
                    paging: {
                        ...oldPaging,
                        currentPage: Math.max(oldPaging.currentPage - 1, 0),
                    },
                };
                return {
                    ...state,
                    [action.tableKey]: newTableState,
                };
            } else {
                return state;
            }
        }
        case ActionTypeNames.GoToPage: {
            const oldPaging = state[action.tableKey].paging;
            if (action.pageNumber >= 0 && action.pageNumber < oldPaging.numberOfPages) {
                const newTableState: TableState = {
                    ...state[action.tableKey],
                    paging: {
                        ...oldPaging,
                        currentPage: Math.min(
                            oldPaging.numberOfPages - 1,
                            Math.max(action.pageNumber, 0)
                        ),
                    },
                };
                return {
                    ...state,
                    [action.tableKey]: newTableState,
                };
            } else {
                return state;
            }
        }
        case ActionTypeNames.ChangeItemsPerPage: {
            const oldPaging = state[action.tableKey].paging;
            const newTableState: TableState = {
                ...state[action.tableKey],
                paging: {
                    ...oldPaging,
                    currentPage: 0,
                    itemsPerPage: action.itemsPerPage,
                    numberOfPages: Math.ceil(oldPaging.numberOfItems / action.itemsPerPage)
                },
            };
            return {
                ...state,
                [action.tableKey]: newTableState,
            };
        }
        case ActionTypeNames.StorePagingState: {
            const oldPaging = state[action.tableKey].paging;
            const newTableState: TableState = {
                ...state[action.tableKey],
                paging: {
                    ...oldPaging,
                    ...action.state,
                },
            };

            return {
                ...state,
                [action.tableKey]: newTableState,
            };
        }
        case ActionTypeNames.StoreSortingState: {
            const oldSorting = state[action.tableKey].sorting;
            const newTableState: TableState = {
                ...state[action.tableKey],
                sorting: {
                    ...oldSorting,
                    ...action.state
                },
            };
            if (action.state.order === undefined) {
                newTableState.sorting = initialState[action.tableKey].sorting;
            }
            return {
                ...state,
                [action.tableKey]: newTableState,
            };
        }
        case ActionTypeNames.Search: {
            const oldPaging = state[action.tableKey].paging;
            const newTableState: TableState = {
                ...state[action.tableKey],
                paging: {
                    ...oldPaging,
                    currentPage: 0,
                },
                search: action.str,
            };
            return {
                ...state,
                [action.tableKey]: newTableState,
            };
        }
        case ActionTypeNames.SetFilter: {
            const oldFilters = state[action.tableKey].filters;
            const oldPaging = state[action.tableKey].paging;
            const newTableState: TableState = {
                ...state[action.tableKey],
                filters: {
                    ...oldFilters,
                    [action.key]: action.selection
                },
                paging: {
                    ...oldPaging,
                    currentPage: 0,
                },
            };
            return {
                ...state,
                [action.tableKey]: newTableState,
            };
        }
        case ActionTypeNames.RemoveFilter: {
            const oldFilters = state[action.tableKey].filters;
            const oldPaging = state[action.tableKey].paging;
            const newTableState: TableState = {
                ...state[action.tableKey],
                filters: {
                    ...oldFilters,
                },
                paging: {
                    ...oldPaging,
                    currentPage: 0,
                },
            };
            delete newTableState.filters[action.key];
            return {
                ...state,
                [action.tableKey]: newTableState,
            };
        }
        default:
            return state;
    }
}
